<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell,
		Button, Modal, Label, Input, Select, Toggle, Spinner, Badge, Checkbox
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import AlertMetricChart from '../../components/admin/AlertMetricChart.svelte';
	import {
		listAlertChannels, createAlertChannel, updateAlertChannel, deleteAlertChannel, testAlertChannel,
		listAlerts, createAlert, updateAlert, deleteAlert, listProbes, getProbe, listServices
	} from '../../AppNetwork';
	import type {
		AlertChannel, AlertChannelType, Alert, AlertInput, AlertType, AlertDirection,
		EvalMode, ServiceStatus, ProbeMetric, Probe, AdminService
	} from '../../AppTypes';

	const TYPE_LABELS: Record<AlertChannelType, string> = {
		email: 'Email',
		teams_webhook: 'Teams webhook',
		teams_workflow: 'Teams workflow (threaded)'
	};

	const DIRECTIONS: { value: AlertDirection; label: string }[] = [
		{ value: 'above', label: 'Above the band' },
		{ value: 'below', label: 'Below the band' },
		{ value: 'both', label: 'Above or below' }
	];
	const EVAL_MODES: { value: EvalMode; label: string }[] = [
		{ value: 'latest', label: 'Latest value' },
		{ value: 'average', label: 'Average of last N' },
		{ value: 'consistent', label: 'N consecutive breaches' }
	];
	const SEVERITIES: { value: ServiceStatus; label: string }[] = [
		{ value: 'degraded', label: 'Degraded' },
		{ value: 'partial_outage', label: 'Partial outage' },
		{ value: 'major_outage', label: 'Major outage' }
	];
	const BUILTIN_METRIC_OPTIONS = [
		{ key: '$up', label: 'Availability ($up)' },
		{ key: '$responseTime', label: 'Response time ($responseTime)' }
	];

	let loading = true;
	let error = '';
	let message = '';

	// --- Channels ---
	let channels: AlertChannel[] = [];
	let showChannelModal = false;
	let editingChannel: AlertChannel | null = null;
	let channelForm = blankChannelForm();
	let savingChannel = false;

	// --- Alert rules ---
	let alerts: Alert[] = [];
	let probes: Probe[] = [];
	let services: AdminService[] = [];
	let probeMetricsMap: Record<number, ProbeMetric[]> = {};
	let showRuleModal = false;
	let editingRule: Alert | null = null;
	let ruleForm = blankRuleForm();
	let savingRule = false;
	let expandedAlertId: number | null = null;

	function toggleExpand(id: number) {
		expandedAlertId = expandedAlertId === id ? null : id;
	}

	function blankChannelForm() {
		return { name: '', type: 'email' as AlertChannelType, target: '', enabled: true };
	}

	function blankRuleForm() {
		return {
			name: '',
			type: 'service_status' as AlertType,
			enabled: true,
			severity: 'degraded' as ServiceStatus,
			serviceId: null as number | null,
			probeId: null as number | null,
			metricKey: '$responseTime',
			direction: 'above' as AlertDirection,
			sigma: 1,
			emaWindowDays: 7,
			minSamples: 5,
			evalMode: 'latest' as EvalMode,
			evalWindow: 3,
			channelIds: [] as number[]
		};
	}

	async function loadAll() {
		loading = true;
		error = '';
		try {
			const [chans, rules, probeList, serviceList] = await Promise.all([
				listAlertChannels(),
				listAlerts(),
				listProbes({ limit: 200 }),
				listServices()
			]);
			channels = chans;
			alerts = rules;
			probes = probeList.items;
			services = serviceList;
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load alerts';
		} finally {
			loading = false;
		}
	}
	onMount(loadAll);

	// --- Channel actions ---
	function openCreateChannel() {
		editingChannel = null; channelForm = blankChannelForm(); error = ''; showChannelModal = true;
	}
	function openEditChannel(channel: AlertChannel) {
		editingChannel = channel; error = '';
		channelForm = { name: channel.name, type: channel.type, target: channel.target, enabled: channel.enabled };
		showChannelModal = true;
	}
	async function saveChannel() {
		savingChannel = true; error = '';
		try {
			if (editingChannel) await updateAlertChannel(editingChannel.id, channelForm);
			else await createAlertChannel(channelForm);
			showChannelModal = false;
			await loadAll();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save channel';
		}
		savingChannel = false;
	}
	async function removeChannel(channel: AlertChannel) {
		if (!confirm(`Delete channel "${channel.name}"? Alerts using it will lose this destination.`)) return;
		await deleteAlertChannel(channel.id);
		await loadAll();
	}
	async function testChannel(channel: AlertChannel) {
		message = `Testing ${channel.name}…`;
		try {
			const result = await testAlertChannel(channel.id);
			message = result.success ? `Test delivered to ${channel.name}.` : `Test to ${channel.name} failed to deliver.`;
		} catch (e) {
			message = e instanceof Error ? e.message : 'Test failed';
		}
	}

	// --- Rule actions ---
	function metricKeyOptions(probeId: number | null): { key: string; label: string }[] {
		const provided = (probeId !== null ? probeMetricsMap[probeId] ?? [] : []).map((m) => ({ key: m.key, label: `${m.label} (${m.key})` }));
		return [...BUILTIN_METRIC_OPTIONS, ...provided];
	}
	async function ensureProbeMetrics(probeId: number | null) {
		if (probeId === null || probeMetricsMap[probeId]) return;
		try {
			const full = await getProbe(probeId);
			probeMetricsMap = { ...probeMetricsMap, [probeId]: full.metrics ?? [] };
		} catch {
			probeMetricsMap = { ...probeMetricsMap, [probeId]: [] };
		}
	}
	$: if (ruleForm.type === 'metric_change' && ruleForm.probeId !== null) {
		ensureProbeMetrics(ruleForm.probeId);
	}

	function openCreateRule() {
		editingRule = null; ruleForm = blankRuleForm();
		if (probes.length > 0) ruleForm.probeId = probes[0].id;
		error = ''; showRuleModal = true;
	}
	function ruleFormFrom(alert: Alert, name: string) {
		return {
			name,
			type: alert.type,
			enabled: alert.enabled,
			severity: alert.severity,
			serviceId: alert.serviceId,
			probeId: alert.probeId,
			metricKey: alert.metricKey ?? '$responseTime',
			direction: alert.direction ?? 'above',
			sigma: alert.sigma ?? 1,
			emaWindowDays: alert.emaWindowDays ?? 7,
			minSamples: alert.minSamples ?? 5,
			evalMode: alert.evalMode,
			evalWindow: alert.evalWindow,
			channelIds: (alert.channels ?? []).map((c) => c.id)
		};
	}
	function openEditRule(alert: Alert) {
		editingRule = alert; error = '';
		ruleForm = ruleFormFrom(alert, alert.name);
		showRuleModal = true;
	}
	function openDuplicateRule(alert: Alert) {
		editingRule = null; // create a new rule from these values
		error = '';
		ruleForm = ruleFormFrom(alert, `${alert.name} (copy)`);
		showRuleModal = true;
	}
	function toggleRuleChannel(id: number) {
		ruleForm.channelIds = ruleForm.channelIds.includes(id)
			? ruleForm.channelIds.filter((c) => c !== id)
			: [...ruleForm.channelIds, id];
	}
	function ruleInput(): AlertInput {
		const base: AlertInput = {
			name: ruleForm.name,
			type: ruleForm.type,
			enabled: ruleForm.enabled,
			channelIds: ruleForm.channelIds
		};
		if (ruleForm.type === 'service_status') {
			return { ...base, serviceId: ruleForm.serviceId };
		}
		return {
			...base,
			severity: ruleForm.severity,
			probeId: ruleForm.probeId,
			metricKey: ruleForm.metricKey,
			direction: ruleForm.direction,
			sigma: Number(ruleForm.sigma),
			emaWindowDays: Number(ruleForm.emaWindowDays),
			minSamples: Number(ruleForm.minSamples),
			evalMode: ruleForm.evalMode,
			evalWindow: Number(ruleForm.evalWindow)
		};
	}
	async function saveRule() {
		savingRule = true; error = '';
		try {
			if (editingRule) await updateAlert(editingRule.id, ruleInput());
			else await createAlert(ruleInput());
			showRuleModal = false;
			await loadAll();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save alert';
		}
		savingRule = false;
	}
	async function removeRule(alert: Alert) {
		if (!confirm(`Delete alert "${alert.name}"?`)) return;
		await deleteAlert(alert.id);
		await loadAll();
	}

	function probeName(id: number | null): string {
		if (id === null) return '—';
		return probes.find((p) => p.id === id)?.name ?? `#${id}`;
	}
	function ruleScope(alert: Alert): string {
		if (alert.type === 'service_status') {
			if (alert.serviceId === null) return 'All services';
			return services.find((s) => s.id === alert.serviceId)?.name ?? `Service #${alert.serviceId}`;
		}
		const arrow = alert.direction === 'above' ? '↑' : alert.direction === 'below' ? '↓' : '↕';
		return `${probeName(alert.probeId)} · ${alert.metricKey} ${arrow} ${alert.sigma ?? 1}σ / ${alert.emaWindowDays ?? 1}d`;
	}
</script>

<PageHeader title="Alerts" description="Alert rules decide when to notify; channels are the destinations they deliver to." />

<div class="space-y-6">
	{#if message}
		<Card class="max-w-none"><p class="text-sm text-gray-500 dark:text-gray-400"><i class="fa-solid fa-circle-info me-2"></i>{message}</p></Card>
	{/if}

	{#if loading}
		<Card class="max-w-none"><div class="p-8 text-center"><i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i><p class="text-gray-500 dark:text-gray-400">Loading…</p></div></Card>
	{:else if error && !showRuleModal && !showChannelModal}
		<Card class="max-w-none"><div class="p-8 text-center"><i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i><p class="text-gray-500 dark:text-gray-400">{error}</p><Button class="mt-4" on:click={loadAll}>Retry</Button></div></Card>
	{:else}
		<!-- Alert rules -->
		<div class="flex items-center justify-between gap-4">
			<h2 class="text-lg font-semibold text-gray-900 dark:text-white">Alert rules</h2>
			<Button on:click={openCreateRule}><i class="fa-solid fa-plus me-2"></i>New Rule</Button>
		</div>
		<Card padding="none" class="max-w-none">
			{#if alerts.length === 0}
				<div class="p-8 text-center"><i class="fa-solid fa-bell text-4xl text-gray-300 dark:text-gray-600 mb-4"></i><p class="text-gray-500 dark:text-gray-400">No alert rules configured</p></div>
			{:else}
				<Table striped divClass="relative overflow-x-auto rounded-lg">
					<TableHead class="bg-gray-50 dark:bg-gray-700">
						<TableHeadCell class="w-8"><span class="sr-only">Expand</span></TableHeadCell>
						<TableHeadCell>Name</TableHeadCell>
						<TableHeadCell>Type</TableHeadCell>
						<TableHeadCell>Status</TableHeadCell>
						<TableHeadCell>Watches</TableHeadCell>
						<TableHeadCell>Channels</TableHeadCell>
						<TableHeadCell>Enabled</TableHeadCell>
						<TableHeadCell>Actions</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each alerts as alert}
							<TableBodyRow>
								<TableBodyCell class="pr-0">
									{#if alert.type === 'metric_change'}
										<button
											type="button"
											class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1"
											aria-label="Toggle metric chart"
											on:click={() => toggleExpand(alert.id)}
										>
											<i class="fa-solid fa-chevron-right transition-transform {expandedAlertId === alert.id ? 'rotate-90' : ''}"></i>
										</button>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="font-medium text-gray-900 dark:text-white">{alert.name}</TableBodyCell>
								<TableBodyCell>
									{#if alert.type === 'service_status'}<Badge color="blue">Service status</Badge>{:else}<Badge color="purple">Metric change</Badge>{/if}
								</TableBodyCell>
								<TableBodyCell>
									{#if !alert.enabled}
										<Badge color="dark">Disabled</Badge>
									{:else if alert.type !== 'metric_change'}
										<span class="text-gray-400">—</span>
									{:else if alert.currentState === 'triggered'}
										<Badge color="red"><i class="fa-solid fa-circle-exclamation me-1"></i>Triggered</Badge>
									{:else}
										<Badge color="green"><i class="fa-solid fa-circle-check me-1"></i>Normal</Badge>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="text-gray-500 dark:text-gray-400 text-sm">{ruleScope(alert)}</TableBodyCell>
								<TableBodyCell class="text-gray-500 dark:text-gray-400 text-sm">
									{(alert.channels ?? []).map((c) => c.name).join(', ') || '—'}
								</TableBodyCell>
								<TableBodyCell>
									{#if alert.enabled}<Badge color="green">On</Badge>{:else}<Badge color="dark">Off</Badge>{/if}
								</TableBodyCell>
								<TableBodyCell>
									<div class="flex gap-1">
										<RowAction icon="fa-pen" label="Edit" on:click={() => openEditRule(alert)} />
										<RowAction icon="fa-copy" label="Duplicate" on:click={() => openDuplicateRule(alert)} />
										<RowAction icon="fa-trash" label="Delete" danger on:click={() => removeRule(alert)} />
									</div>
								</TableBodyCell>
							</TableBodyRow>
							{#if alert.type === 'metric_change' && expandedAlertId === alert.id}
								<TableBodyRow>
									<TableBodyCell colspan={8} class="!p-0 bg-gray-50 dark:bg-gray-800">
										<AlertMetricChart alertId={alert.id} />
									</TableBodyCell>
								</TableBodyRow>
							{/if}
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Card>

		<!-- Channels -->
		<div class="flex items-center justify-between gap-4 pt-2">
			<h2 class="text-lg font-semibold text-gray-900 dark:text-white">Channels</h2>
			<Button on:click={openCreateChannel}><i class="fa-solid fa-plus me-2"></i>New Channel</Button>
		</div>
		<Card padding="none" class="max-w-none">
			{#if channels.length === 0}
				<div class="p-8 text-center"><i class="fa-solid fa-paper-plane text-4xl text-gray-300 dark:text-gray-600 mb-4"></i><p class="text-gray-500 dark:text-gray-400">No alert channels configured</p></div>
			{:else}
				<Table striped divClass="relative overflow-x-auto rounded-lg">
					<TableHead class="bg-gray-50 dark:bg-gray-700">
						<TableHeadCell>Name</TableHeadCell>
						<TableHeadCell>Type</TableHeadCell>
						<TableHeadCell>Target</TableHeadCell>
						<TableHeadCell>Enabled</TableHeadCell>
						<TableHeadCell>Actions</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each channels as channel}
							<TableBodyRow>
								<TableBodyCell class="font-medium text-gray-900 dark:text-white">{channel.name}</TableBodyCell>
								<TableBodyCell class="text-gray-500 dark:text-gray-400">{TYPE_LABELS[channel.type]}</TableBodyCell>
								<TableBodyCell class="max-w-xs truncate text-gray-500 dark:text-gray-400">{channel.target}</TableBodyCell>
								<TableBodyCell>
									{#if channel.enabled}<Badge color="green">On</Badge>{:else}<Badge color="dark">Off</Badge>{/if}
								</TableBodyCell>
								<TableBodyCell>
									<div class="flex gap-1">
										<RowAction icon="fa-paper-plane" label="Send test" on:click={() => testChannel(channel)} />
										<RowAction icon="fa-pen" label="Edit" on:click={() => openEditChannel(channel)} />
										<RowAction icon="fa-trash" label="Delete" danger on:click={() => removeChannel(channel)} />
									</div>
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Card>
	{/if}
</div>

<!-- Rule modal -->
<Modal bind:open={showRuleModal} title={editingRule ? 'Edit Alert Rule' : 'New Alert Rule'} size="md">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={ruleForm.name} placeholder="e.g. API latency anomaly" /></div>
		<div><Label class="mb-1">Type</Label>
			<Select bind:value={ruleForm.type}>
				<option value="service_status">Service status change</option>
				<option value="metric_change">Statistical metric change</option>
			</Select>
		</div>

		{#if ruleForm.type === 'service_status'}
			<div><Label class="mb-1">Scope</Label>
				<Select bind:value={ruleForm.serviceId}>
					<option value={null}>All services</option>
					{#each services as s}<option value={s.id}>{s.name}</option>{/each}
				</Select>
				<p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Fires whenever a covered service changes status (outage, degraded, recovery).</p>
			</div>
		{:else}
			<div class="grid grid-cols-2 gap-3">
				<div><Label class="mb-1">Probe</Label>
					<Select bind:value={ruleForm.probeId}>
						{#each probes as p}<option value={p.id}>{p.name}</option>{/each}
					</Select>
				</div>
				<div><Label class="mb-1">Metric</Label>
					<Select bind:value={ruleForm.metricKey}>
						{#each metricKeyOptions(ruleForm.probeId) as opt}<option value={opt.key}>{opt.label}</option>{/each}
					</Select>
				</div>
			</div>
			<div class="grid grid-cols-3 gap-3">
				<div><Label class="mb-1">Direction</Label>
					<Select bind:value={ruleForm.direction}>
						{#each DIRECTIONS as d}<option value={d.value}>{d.label}</option>{/each}
					</Select>
				</div>
				<div><Label class="mb-1">Sigma (σ)</Label><Input type="number" step="0.1" min="0" bind:value={ruleForm.sigma} /></div>
				<div><Label class="mb-1">EMA window (days)</Label><Input type="number" min="1" bind:value={ruleForm.emaWindowDays} /></div>
			</div>
			<div class="grid grid-cols-3 gap-3">
				<div><Label class="mb-1">Eval mode</Label>
					<Select bind:value={ruleForm.evalMode}>
						{#each EVAL_MODES as m}<option value={m.value}>{m.label}</option>{/each}
					</Select>
				</div>
				<div><Label class="mb-1">Window (N)</Label><Input type="number" min="1" bind:value={ruleForm.evalWindow} disabled={ruleForm.evalMode === 'latest'} /></div>
				<div><Label class="mb-1">Min samples</Label><Input type="number" min="0" bind:value={ruleForm.minSamples} /></div>
			</div>
			<div><Label class="mb-1">Report as severity</Label>
				<Select bind:value={ruleForm.severity}>
					{#each SEVERITIES as s}<option value={s.value}>{s.label}</option>{/each}
				</Select>
			</div>
			<p class="text-xs text-gray-500 dark:text-gray-400">
				Fires when the metric deviates {ruleForm.sigma}σ {DIRECTIONS.find((d) => d.value === ruleForm.direction)?.label.toLowerCase()}
				of its exponential moving average over the last {ruleForm.emaWindowDays} days.
			</p>
		{/if}

		<div>
			<Label class="mb-1">Channels</Label>
			{#if channels.length === 0}
				<p class="text-xs text-gray-500 dark:text-gray-400">No channels yet — create one below to deliver this alert.</p>
			{:else}
				<div class="space-y-1">
					{#each channels as channel}
						<Checkbox checked={ruleForm.channelIds.includes(channel.id)} on:change={() => toggleRuleChannel(channel.id)}>
							{channel.name} <span class="text-gray-400 text-xs ms-1">({TYPE_LABELS[channel.type]})</span>
						</Checkbox>
					{/each}
				</div>
			{/if}
		</div>

		<Toggle bind:checked={ruleForm.enabled}>Enabled</Toggle>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={saveRule} disabled={savingRule || !ruleForm.name || (ruleForm.type === 'metric_change' && !ruleForm.probeId)}>
				{#if savingRule}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showRuleModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>

<!-- Channel modal -->
<Modal bind:open={showChannelModal} title={editingChannel ? 'Edit Channel' : 'New Channel'} size="md">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={channelForm.name} /></div>
		<div><Label class="mb-1">Type</Label>
			<Select bind:value={channelForm.type}>
				<option value="email">Email</option>
				<option value="teams_webhook">Microsoft Teams webhook</option>
				<option value="teams_workflow">Microsoft Teams workflow (threaded)</option>
			</Select>
		</div>
		<div>
			<Label class="mb-1">{channelForm.type === 'email' ? 'Email address' : channelForm.type === 'teams_workflow' ? 'Flow HTTP URL' : 'Webhook URL'}</Label>
			<Input bind:value={channelForm.target} />
			{#if channelForm.type === 'teams_workflow'}
				<p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
					A custom Power Automate flow that accepts
					<code class="font-mono">{'{ title, text, replyToMessageId? }'}</code> and responds with
					<code class="font-mono">{'{ "messageId": "..." }'}</code>. See the README for the flow setup.
				</p>
			{/if}
		</div>
		<Toggle bind:checked={channelForm.enabled}>Enabled</Toggle>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={saveChannel} disabled={savingChannel || !channelForm.name || !channelForm.target}>
				{#if savingChannel}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showChannelModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
